import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/Card';

const GithubAppCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const installationId = searchParams.get('installation_id');
        if (!installationId) throw new Error('No installation ID received from GitHub');

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error: updateError } = await supabase
          .from('users')
          .update({ github_installation_id: installationId, updated_at: new Date().toISOString() })
          .eq('id', user.id);

        if (updateError) throw updateError;

        setStatus('success');
        setTimeout(() => navigate('/dashboard'), 2000);

      } catch (err) {
        console.error('GitHub app installation error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        setStatus('error');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const statusContent = {
    loading: {
      icon: <Loader2 className="h-12 w-12 animate-spin text-primary" />,
      title: "Processing GitHub Integration...",
      description: "Please wait while we set up your account.",
      button: null,
    },
    error: {
      icon: <XCircle className="h-12 w-12 text-destructive" />,
      title: "Installation Failed",
      description: error || "There was a problem installing the GitHub app.",
      button: <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>,
    },
    success: {
      icon: <CheckCircle className="h-12 w-12 text-green-500" />,
      title: "GitHub App Installed Successfully!",
      description: "You can now use Codii with your GitHub repositories. Redirecting...",
      button: <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>,
    },
  };

  const currentStatus = statusContent[status];

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
            {currentStatus.icon}
          </div>
          <CardTitle>{currentStatus.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-6">{currentStatus.description}</p>
          {currentStatus.button}
        </CardContent>
      </Card>
    </div>
  );
};

export default GithubAppCallback;
